import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Grid,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

interface AccountSettingsProps {
  user: any;
  profileData: any;
  isEditing: boolean;
  error: string;
  success: string;
  onProfileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onProfileSubmit: (e: React.FormEvent) => void;
  onEdit: () => void;
  onCancel: () => void;
  isUpdating: boolean;
}

const AccountSettings: React.FC<AccountSettingsProps> = ({
  user,
  profileData,
  isEditing,
  error,
  success,
  onProfileChange,
  onProfileSubmit,
  onEdit,
  onCancel,
  isUpdating,
}) => {
  return (
    <Box component="form" onSubmit={onProfileSubmit}>
      {/* Email Verification Status */}
      {!user?.is_verified && (
        <Box sx={{ mb: 3 }}>
          <Alert 
            severity="warning" 
            icon={<WarningIcon />}
            sx={{ borderRadius: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => window.location.href = '/verify-email'}
              >
                Verify Now
              </Button>
            }
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Email Verification Required
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please verify your email address to access all features.
            </Typography>
          </Alert>
        </Box>
      )}
      
      {user?.is_verified && (
        <Box sx={{ mb: 3 }}>
          <Alert 
            severity="success" 
            icon={<CheckIcon />}
            sx={{ borderRadius: 2 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Email Verified ✓
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your email address is verified and you have full access to all features.
            </Typography>
          </Alert>
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="First Name"
            name="first_name"
            value={profileData.first_name}
            onChange={onProfileChange}
            placeholder="Enter your first name"
            disabled={!isEditing}
            sx={{ 
              borderRadius: 2,
              mb: 2,
              '& .MuiInputBase-input': {
                transition: 'none'
              }
            }}
          />
        </Grid>
        
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Last Name"
            name="last_name"
            value={profileData.last_name}
            onChange={onProfileChange}
            placeholder="Enter your last name"
            disabled={!isEditing}
            sx={{ 
              borderRadius: 2,
              mb: 2,
              '& .MuiInputBase-input': {
                transition: 'none'
              }
            }}
          />
        </Grid>
        
        <Grid size={12}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={profileData.email}
            onChange={onProfileChange}
            placeholder="Enter your email"
            disabled={!isEditing}
            sx={{ 
              borderRadius: 2,
              mb: 2,
              '& .MuiInputBase-input': {
                transition: 'none'
              }
            }}
          />
        </Grid>
      </Grid>
      
      {/* Alerts */}
      <Box sx={{ mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ borderRadius: 2, mb: 2 }}>
            {success}
          </Alert>
        )}
      </Box>
      
      {/* Action Buttons */}
      {isEditing && (
        <Box sx={{ 
          mt: 3, 
          display: 'flex', 
          gap: 2, 
          justifyContent: 'flex-end',
          alignItems: 'center'
        }}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={onCancel}
            disabled={isUpdating}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={isUpdating ? undefined : <SaveIcon />}
            disabled={isUpdating}
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #7b8cff 0%, #ff5b8d 100%)',
              }
            }}
          >
            {isUpdating ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      )}

    </Box>
  );
};

export default AccountSettings;
